import { useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { RootRoute } from "../root-route";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { HelpCircle, ChevronDown } from "lucide-react";

export const FaqRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/faq",
  component: Faq,
  head: () => ({
    meta: [
      { title: "Vanliga frågor om bevattningsförbud" },
      { name: "description", content: "Svar på vanliga frågor om bevattningsförbud — vad de innebär, när de införs och vad som gäller." },
    ],
    links: [
      { rel: "canonical", href: "https://bevattningskartan.se/faq" },
    ],
  }),
});

const faqItems = [
  {
    question: "Vad är ett bevattningsförbud?",
    answer:
      "Ett bevattningsförbud är ett beslut från en kommun eller VA-huvudman som begränsar eller förbjuder vattning med dricksvatten under en period. Syftet är att spara på vattentillgången när tillgången är låg, till exempel under en långvarig torka.",
  },
  {
    question: "Vad är skillnaden mellan förbud och rekommendation?",
    answer:
      "Ett förbud är bindande och innebär att vissa användningar av dricksvatten är otillåtna. En rekommendation är en uppmaning om att vara sparsam med vattnet, men det finns ingen sanktion om du inte följer den. Båda syftar till att minska vattenförbrukningen.",
  },
  {
    question: "Vad får jag inte göra under ett bevattningsförbud?",
    answer:
      "Vad som är förbjudet varierar mellan kommuner. Vanliga exempel är att vattna gräsmattan, tvätta bilen, fylla poolen eller spola gatan med dricksvatten. Kontrollera alltid den lokala informationen från din kommun eller VA-huvudman för exakt vad som gäller hos dig.",
  },
  {
    question: "Får jag vattna trädgården med vatten ur brunn eller sjö?",
    answer:
      "Bevattningsförbud gäller oftast bara dricksvatten från det kommunala vattennätet. Om du har en egen brunn eller tar vatten från en sjö kan det vara tillåtet, men reglerna skiljer sig åt. Läs alltid den lokala informationen för att vara säker.",
  },
  {
    question: "Vem beslutar om bevattningsförbud?",
    answer:
      "Beslutet fattas vanligtvis av kommunen eller den kommunala VA-huvudmannen. I vissa fall kan det vara ett kommunalförbund eller ett privat vattenbolag som ansvarar för vattendistributionen.",
  },
  {
    question: "Hur länge varar ett bevattningsförbud?",
    answer:
      "Det varierar. Ett förbud kan gälla från några dagar upp till flera månader, beroende på väder, vattentillgång och förbrukning. Beslutet hävs när situationen har förbättrats.",
  },
  {
    question: "Vad händer om jag bryter mot förbudet?",
    answer:
      "Vanligtvis får du en varning om du bryter mot förbudet. I vissa kommuner kan det i teorin leda till att ditt vatten stängs av eller strypas, men det tillämpas mycket sällan i praktiken. Hur det hanteras skiljer sig åt mellan kommuner och VA-huvudmän.",
  },
  {
    question: "Hur vet jag om det råder bevattningsförbud där jag bor?",
    answer:
      "Kontrollera din kommuns eller VA-huvudmans webbplats. De publicerar information om eventuella förbud och rekommendationer, ofta med tydliga instruktioner om vad som gäller.",
  },
  {
    question: "Varför införs bevattningsförbud?",
    answer:
      "Bevattningsförbud införs för att säkerställa att det finns tillräckligt med dricksvatten för nödvändig användning, som matlagning, dryck och hygien. Under torra perioder kan förbrukningen öka samtidigt som vattentillgången minskar, till exempel låga grundvattennivåer.",
  },
  {
    question: "Gäller informationen på den här sidan för alla kommuner?",
    answer:
      "Nej. Regler för bevattningsförbud beslutas lokalt och kan skilja sig åt mellan kommuner. Informationen på den här sidan är allmän vägledning om vad bevattningsförbud innebär. För aktuella regler i just din kommun måste du kontrollera kommunens eller VA-huvudmannens egen information.",
  },
];

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-display text-lg text-foreground transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        <span>{question}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-5 pb-5 text-muted-foreground leading-relaxed">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

function Faq() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        <article className="max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary text-primary-foreground">
              <HelpCircle className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <h1 className="font-display text-4xl sm:text-5xl text-foreground">
              Vanliga frågor om bevattningsförbud
            </h1>
          </div>

          <p className="mt-4 text-muted-foreground">
            Här hittar du svar på vanliga frågor om vad bevattningsförbud är,
            när de införs och vad som gäller. Detta är allmän information om
            bevattningsförbud — inte om själva webbplatsen{" "}
            <span className="font-medium text-foreground">Bevattningskartan.se</span>.
          </p>

          <div className="mt-10 space-y-4">
            {faqItems.map((item, index) => (
              <FaqItem key={index} question={item.question} answer={item.answer} />
            ))}
          </div>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}
